import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { AuthApiService } from '../backend/services/auth-api.service';
import { ROLES } from '../../app-const';
import { resolveSuperAdminLandingRoute } from '../../layouts/sidebar/super-admin-nav';

export interface MgUser {
  id?: number;
  username?: string;
  email?: string;
  full_name?: string;
  user_type?: { id: number; name: string } | null;
  platform_role?: string | null;
  roles?: number[] | null;
  permissions?: string[];
  landing_route?: string | null;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  currentUserSubject: BehaviorSubject<MgUser | null>;
  public currentUser: Observable<MgUser | null>;

  private token: string | undefined | null;
  private identityRequest$: Observable<MgUser | null> | null = null;
  private loggingOut = false;

  get isLoggingOut(): boolean {
    return this.loggingOut;
  }

  public get _token(): string {
    return this.token || this.readStoredToken(sessionStorage) || this.readStoredToken(localStorage) || '';
  }

  constructor(
    private router: Router,
    private authApi: AuthApiService
  ) {
    // Persist the token only. Permission-bearing identity must be loaded from
    // the server for each browser session so revoked grants are not restored
    // from stale local storage.
    try {
      localStorage.removeItem('currentUser');
    } catch {
      // Storage can be unavailable in hardened browser contexts.
    }
    this.currentUserSubject = new BehaviorSubject<MgUser | null>(null);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  private readStoredToken(storage: Storage): string | null {
    try {
      const raw = storage.getItem('vultToken');
      if (!raw || raw === 'null') {
        return null;
      }
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  private persistAuthToken(token: string, rememberMe: boolean): void {
    const serialized = JSON.stringify(token);
    try {
      if (rememberMe) {
        localStorage.setItem('vultToken', serialized);
        sessionStorage.removeItem('vultToken');
      } else {
        sessionStorage.setItem('vultToken', serialized);
        localStorage.removeItem('vultToken');
      }
    } catch {
      // Storage error fallback
    }
  }

  clearAuthenticationState(clearToken = true): void {
    if (clearToken) {
      this.token = null;
      localStorage.removeItem('vultToken');
      sessionStorage.removeItem('vultToken');
    }
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }

  login(identity: string, password: string, rememberMe = false): Observable<any> {
    return this.authApi.login({ identity, password }).pipe(
      map((response) => {
        if (response && response.data?.token) {
          const token = String(response.data.token);
          this.clearAuthenticationState(false);
          this.token = token;
          this.persistAuthToken(token, rememberMe);
        }
        return response;
      })
    );
  }

  verifyMfaChallenge(challengeToken: string, code?: string, recoveryCode?: string, rememberMe = false): Observable<any> {
    return this.authApi.verifyMfaChallenge({ challenge_token: challengeToken, code, recovery_code: recoveryCode }).pipe(
      map((response) => {
        if (response && response.data?.token) {
          const token = String(response.data.token);
          this.clearAuthenticationState(false);
          this.token = token;
          this.persistAuthToken(token, rememberMe);
        }
        return response;
      })
    );
  }

  identity(force = false): Observable<MgUser | null> {
    if (!this._token) {
      return of(null);
    }

    if (this.identityRequest$) {
      return this.identityRequest$;
    }

    if (this.currentUserSubject.value && !force) {
      return this.currentUserSubject;
    }

    const request$ = this.authApi.getCurrentUser().pipe(
      distinctUntilChanged(),
      map((res) => {
        if (res && res.data?.user) {
          const apiUser = res.data.user;
          const platformRole = (apiUser as { platform_role?: string | null })?.platform_role;

          if (platformRole === 'super_admin') {
            const rawPerms = Array.isArray(apiUser?.permissions) ? apiUser.permissions : [];
            const permissions = rawPerms.map((p: any) => String(p || '').trim()).filter(Boolean);
            const landingRoute = resolveSuperAdminLandingRoute(
              permissions,
              (apiUser as { landing_route?: string })?.landing_route
            );

            const resolvedUser: MgUser = {
              ...apiUser,
              roles: [ROLES.SuperAdmin],
              permissions,
              platform_role: 'super_admin',
              landing_route: landingRoute,
            };

            this.currentUserSubject.next(resolvedUser);
            return resolvedUser;
          }

          // If user logged in does not have super_admin platform authority
          this.clearAuthenticationState(true);
          return null;
        }

        this.clearAuthenticationState(true);
        return null;
      }),
      catchError(() => {
        this.clearAuthenticationState(true);
        return of(null);
      }),
      finalize(() => {
        if (this.identityRequest$ === request$) {
          this.identityRequest$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.identityRequest$ = request$;
    return request$;
  }

  get isSuperAdmin(): boolean {
    return this.currentUserSubject.value?.platform_role === 'super_admin';
  }

  public get currentUserValue(): MgUser | null {
    return this.currentUserSubject.value;
  }

  hasPermission(required: string | string[] | null | undefined): boolean {
    if (!required) {
      return true;
    }

    const userPermissions = new Set((this.currentUserSubject.value?.permissions || []).filter(Boolean));
    if (userPermissions.has('*')) {
      return true;
    }

    const requiredPermissions = (Array.isArray(required) ? required : [required])
      .map((permission) => String(permission || '').trim())
      .filter(Boolean);

    if (requiredPermissions.length === 0) {
      return true;
    }

    return requiredPermissions.some((permission) => userPermissions.has(permission));
  }

  logout(): void {
    this.loggingOut = true;
    this.authApi.logout().pipe(
      finalize(() => {
        this.clearAuthenticationState(true);
        this.loggingOut = false;
        this.router.navigate(['/login']);
      })
    ).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  redirectUser(): void {
    const user = this.currentUserSubject.value;
    if (user && user.platform_role === 'super_admin') {
      const landing = resolveSuperAdminLandingRoute(user.permissions, user.landing_route);
      this.router.navigateByUrl(landing);
    } else {
      this.router.navigate(['/login']);
    }
  }
}
